import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, Stack, Modal } from "@mui/material";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { SizingWrapper, SizingWrapperStyle } from "@/components/Wrapper";
import { CSelect } from "@/components/Select/CSelect";
import { CTextField } from "@/components/TextField/CTextField";
import { CTextFieldPastDays } from "@/components/TextField/CTextFieldPastDays";
import {
  checkKaokuNumberSearchDisabled,
  createAcquireMultipleBookHandoffData,
  getInitialPictureRequestOptions,
  getPrefectureSelectObject,
  getRealEstateTypeEngSelectObject,
  pastDaysDescription,
  transformChibanKaokuNumber,
} from "@/utils/utilsAcquireBook";
import { Button, ButtonVariantOption } from "@/components/Button";
import { type AcquireBookForm } from "@/features/acquireBook/hooks/useAcquireBookForm";
import { useNavigate } from "react-router-dom";
import type { ISingleBookAcquireDialogData } from "@/types/singleBookAcquireDialog";
import {
  AcquireBookModalFormSchema,
  createPictureCheckOptionsValidator,
} from "@/schemas/acquirebook";
import { KaokuNumberSearchModal } from "./KaokuNumberSearchModal";
import { type PropertySelectionRow } from "@/features/map/types";
import {
  type AcquireMultipleBookLocationState,
  type AcquireBookModalFormData,
  type BookTypeEng,
} from "@/types/acquirebook";
import { toast } from "react-toastify";
import { SelectLocationModal } from "./SelectLocationModal";
import { CAutocompleteMultiFreeSoloTextField } from "../TextField/CAutocompleteMultiFreeSoloTextField";
import { preventSpaceKey } from "@/utils/utils";
import { useUserConfig } from "@/hooks/useUserConfig";
import { Label } from "@/components/Label";
import { useReuseInfoGetMutation } from "@/hooks/useReuseInfoGetMutation";
import { usePictureCheckOptionsManager } from "@/hooks/usePictureCheckOptionsManager";
import { AcquireBookSelectionSection } from "./AcquireBookSelectionSection";
import { useFeatureFlags } from "@/configs/featureFlag";
import { CCheckbox } from "../Checkbox/CCheckbox";
import { ConfirmAcquireBookDialog } from "./ConfirmAcquireBookDialog";

interface IPropsAcquireBookFormModal {
  acquireBookForm: AcquireBookForm;
  singleBookAcquireDialogData: ISingleBookAcquireDialogData;
  isReadOnly: boolean;
  isKaokuNumberSearchModal: boolean;
  handleClose: (() => void) | undefined;
}

/**
 * 登記1件取得UIのモーダル版
 * @param props
 */
export const AcquireBookFormModal: React.FC<IPropsAcquireBookFormModal> = (
  props
) => {
  const {
    singleBookAcquireDialogData,
    acquireBookForm,
    isReadOnly,
    isKaokuNumberSearchModal,
    handleClose,
  } = props;

  const { ownerInfoViaScraping, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();

  // ページ遷移のためのナビゲーションを設定。
  const navigate = useNavigate();

  // 「家屋番号検索」モーダル表示非表示管理State
  const [kaokuNumberSearchModalIsOpen, setKaokuNumberSearchModalIsOpen] =
    useState<boolean>(false);

  const [disableConfirmModalCancelButton, setDisableConfirmModalCancelButton] =
    useState<boolean>(false);

  const [selectLocationDialogIsOpen, setSelectLocationDialogIsOpen] =
    useState<boolean>(false);

  // 確認画面のモーダル表示非表示管理State
  const [openModalFlag, setOpenModalFlag] = React.useState(false);

  // 再利用情報取得API用のカスタムフック
  const {
    handleReuseInfoGetAPI,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  } = useReuseInfoGetMutation();

  // 外部データが取得済みかどうかを判定
  const isReuseDataFetched = reuseInfoGetMutateAPIResponseList.length > 0;

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<AcquireBookModalFormData>({
    mode: "all",
    defaultValues: {
      prefectureId: String(singleBookAcquireDialogData.prefecturesId),
      prefectureName: singleBookAcquireDialogData.prefectures,
      realEstateTypeEng: singleBookAcquireDialogData.realEstateTypeEng,
      location: singleBookAcquireDialogData.address,
      chibanKaokuNumbers: [
        transformChibanKaokuNumber(
          singleBookAcquireDialogData.chiban +
            singleBookAcquireDialogData.kaokuNumber
        ),
      ],
      pictureCheckOptions: getInitialPictureRequestOptions({
        realEstateTypeEng: singleBookAcquireDialogData.realEstateTypeEng,
        hasReuseData: isReuseDataFetched,
      }),
      identifyName: "",
      pastDays: undefined, // undefinedの場合は0になるようにCTextFieldPastDaysで変換される
      withOwnerAnalyze: false,
      withAllMattersAnalyze: false,
      requestClosureBook: false,
    },
    resolver: zodResolver(
      AcquireBookModalFormSchema.extend({
        pictureCheckOptions:
          createPictureCheckOptionsValidator(isReuseDataFetched), // 外部データ取得状態に応じたバリデーションを適用
      })
    ),
  });

  // 値の更新を監視
  const prefectureId = reactHookForm.watch("prefectureId");
  const prefectureName = reactHookForm.watch("prefectureName");
  const location = reactHookForm.watch("location");
  const realEstateTypeEng = reactHookForm.watch("realEstateTypeEng");
  const chibanKaokuNumbers = reactHookForm.watch("chibanKaokuNumbers");
  const pastDays = reactHookForm.watch("pastDays");
  const requestClosureBook = reactHookForm.watch("requestClosureBook");
  const pictureCheckOptions = reactHookForm.watch("pictureCheckOptions");

  // ユーザー設定取得
  // 一度でも入力があったら更新しないようにする
  useUserConfig({
    onUserSettingsLoaded: (data) => {
      reactHookForm.setValue("pastDays", data.pictureRequestPastDays);
    },
    enableCallbackOnRefetch: !reactHookForm.formState.isDirty,
  });

  // 閉鎖登記簿請求をチェックしたら所有者事項解析を非活性に
  useEffect(() => {
    if (requestClosureBook) {
      reactHookForm.setValue("withOwnerAnalyze", false);
    } else {
      reactHookForm.setValue("withAllMattersAnalyze", false);
    }
  }, [requestClosureBook]);

  // 登記図面に関連するフォーム状態を管理するカスタムフック
  const {
    disabledReuseRequest,
    getReuseData,
    updatePictureCheckOptionsForm,
    getReusePictures,
  } = usePictureCheckOptionsManager({
    reactHookForm,
    initialDisabledReuseRequest: realEstateTypeEng === "",
    initialPictureCheckOptions: pictureCheckOptions,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  });

  /**
   * 物件リストに物件を追加する処理。
   * 登記取得一括画面に遷移する。
   * @param {PropertySelectionRow[]} rows
   */
  const appendPropertySelectionRows = (rows: PropertySelectionRow[]): void => {
    const states = createAcquireMultipleBookHandoffData(
      "LAND",
      chibanKaokuNumbers,
      String(prefectureId),
      prefectureName,
      location,
      rows
    );

    // 一括取得の画面に地番を含めて値を受け渡し、登記取得一括画面に遷移する。
    navigate("/acquiremultiplebooks", { state: states });
  };

  // 登記および図面取得ボタンが押された時の処理
  const handleAcquireButtonClick = async (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): Promise<void> => {
    await reactHookForm.handleSubmit(
      (data) => {
        const reusePictures = getReusePictures();
        acquireBookForm.handleSubmit(
          data.chibanKaokuNumbers, // 地番 + 家屋番号
          data.prefectureId, // 都道府県コード (1 - 47の数字文字列)
          "", // 市町村コード (数字文字列) (locationFullがある場合, 空欄可)
          "", // locationWithoutCity (yyy町) (locationFullがある場合, 空欄可)
          data.location, // locationFull (xxx市yyy町)
          data.pastDays, // 経過日数
          data.realEstateTypeEng, // 不動産種別 (大文字英語)
          data.pictureCheckOptions, // オプション設定値
          data.withOwnerAnalyze, // 所有者事項解析
          data.withAllMattersAnalyze, // 全部事項解析
          data.identifyName,
          cancelLoading,
          setDisableConfirmModalCancelButton,
          reusePictures,
          data.requestClosureBook
        );
      },
      () => {
        cancelLoading();
      }
    )();
  };

  // 再利用情報取得ボタンが押された時の処理
  const handleReuseInfoGetClick = async (
    cancelLoading: () => void
  ): Promise<void> => {
    const newReuseInfoGetMutateAPIResponseList = await handleReuseInfoGetAPI(
      {
        real_estate_type: realEstateTypeEng,
        pref_code: prefectureId,
        location_full: location,
        past_days: pastDays,
      },
      chibanKaokuNumbers,
      cancelLoading
    );
    updatePictureCheckOptionsForm(
      realEstateTypeEng,
      prefectureId,
      location,
      chibanKaokuNumbers,
      newReuseInfoGetMutateAPIResponseList.length === 0, // 再利用情報がない場合は必ず非表示
      true // チェック状態を保持する
    );
  };

  return (
    <>
      <Stack spacing={2} sx={{ marginTop: "8px" }}>
        <Stack sx={{ mt: "1em" }} spacing={2} direction={"row"}>
          <Stack>
            <Label required={true}>不動産種別</Label>
            <CSelect
              control={reactHookForm.control}
              name="realEstateTypeEng"
              label="不動産種別"
              options={getRealEstateTypeEngSelectObject()}
              isClearable={false}
              callbackOnChange={(newRealEstateTypeEng) => {
                updatePictureCheckOptionsForm(
                  newRealEstateTypeEng,
                  prefectureId,
                  location,
                  chibanKaokuNumbers,
                  true, // ラジオボタン非表示
                  true // チェック状態を保持する
                );
              }}
              disabled={isReadOnly}
            />
          </Stack>
        </Stack>
        <Stack spacing={2} direction={"row"}>
          <SizingWrapper>
            <Label required={true}>都道府県</Label>
            <CSelect
              control={reactHookForm.control}
              name="prefectureId"
              label="都道府県"
              options={getPrefectureSelectObject()}
              callbackOnChange={(newPrefectureId) => {
                updatePictureCheckOptionsForm(
                  realEstateTypeEng,
                  newPrefectureId,
                  location,
                  chibanKaokuNumbers,
                  true, // ラジオボタン非表示
                  true // チェック状態を保持する
                );
              }}
              isClearable={false}
              disabled={isReadOnly}
            />
          </SizingWrapper>
          <Grid item sx={{ pt: "2.5em" }}>
            <Button
              label="所在選択"
              onClick={() => {
                if (prefectureId === "") {
                  // 都道府県が選択されていない場合
                  toast.info("都道府県を選択してください");
                } else {
                  setSelectLocationDialogIsOpen(true);
                }
              }}
              size={SizingWrapperStyle.SMALL}
              variant={ButtonVariantOption.Outlined}
              disabled={isReadOnly}
            />
          </Grid>
        </Stack>
        <Stack spacing={2} direction={"row"}>
          <Stack sx={{ flexGrow: 1 }}>
            <Label required={true}>所在</Label>
            <CTextField
              control={reactHookForm.control}
              name="location"
              label="所在"
              size={SizingWrapperStyle.INHERIT}
              callbackOnChange={(event) => {
                updatePictureCheckOptionsForm(
                  realEstateTypeEng,
                  prefectureId,
                  event.target.value,
                  chibanKaokuNumbers,
                  true, // ラジオボタン非表示
                  true // チェック状態を保持する
                );
              }}
              disabled={isReadOnly}
            />
          </Stack>
          <Stack sx={{ flexGrow: 1 }}>
            <Label required={true}>地番または家屋番号</Label>
            <CAutocompleteMultiFreeSoloTextField
              control={reactHookForm.control}
              name="chibanKaokuNumbers"
              getValues={reactHookForm.getValues}
              label="地番または家屋番号"
              size={SizingWrapperStyle.INHERIT}
              readOnly={isReadOnly}
              onKeyDown={preventSpaceKey}
              handleTransformValue={transformChibanKaokuNumber}
              callbackOnChange={(newValues) => {
                updatePictureCheckOptionsForm(
                  realEstateTypeEng,
                  prefectureId,
                  location,
                  newValues,
                  true, // ラジオボタン非表示
                  true // チェック状態を保持する
                );
                if (newValues.length > 1) {
                  // 地番または家屋番号が複数選択の場合は、不必要なバリデーションはクリア
                  reactHookForm.clearErrors(["pictureCheckOptions"]);
                }
              }}
              disabled={isReadOnly}
            />
          </Stack>
        </Stack>
        <Stack spacing={2} direction={"row"}>
          <Stack sx={{ mt: "1em" }} spacing={2} direction={"row"}>
            <Stack>
              <Label>識別名</Label>
              <CTextFieldIdentifyName
                name={"identifyName"}
                control={reactHookForm.control}
                size={SizingWrapperStyle.LARGE}
              />
            </Stack>
          </Stack>
          <Stack>
            <Label required={true}>経過日数</Label>
            <CTextFieldPastDays
              name="pastDays"
              control={reactHookForm.control}
              size={SizingWrapperStyle.INHERIT}
              tooltipText={pastDaysDescription}
            />
          </Stack>
          {isKaokuNumberSearchModal && (
            <Stack direction="row" alignItems={"center"}>
              <Button
                disabled={checkKaokuNumberSearchDisabled({
                  realEstateTypeEng,
                  prefectureId,
                  location,
                  chibanKaokuNumbers,
                })}
                label="家屋番号検索"
                onClick={() => {
                  setKaokuNumberSearchModalIsOpen(true);
                }}
                size={SizingWrapperStyle.SMALL}
                variant={ButtonVariantOption.Contained}
              />
            </Stack>
          )}
        </Stack>
        <Stack spacing={2} direction={"row"}>
          <Stack sx={{ mt: "1em" }} spacing={2} direction={"row"}>
            <Stack>
              <Label>閉鎖登記簿</Label>
              <Box ml={2}>
                <CCheckbox
                  name="requestClosureBook"
                  label="閉鎖登記簿請求"
                  control={reactHookForm.control}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Box sx={{ mt: 3 }}>
          <FormProvider {...reactHookForm}>
            <AcquireBookSelectionSection
              disabled={chibanKaokuNumbers.length > 1}
              isDataFetched={isReuseDataFetched}
              onClickReuseInfoGetButton={(_event, cancelLoading) => {
                void handleReuseInfoGetClick(cancelLoading);
              }}
              getReuseData={getReuseData}
              disabledReuseRequestButton={disabledReuseRequest}
              handleCustomOptionChange={(event, key) => {
                // NOTE: ページとモーダルで共通の処理はここに書かない
                if (key === "SHOYUSHAJIKO" && !event) {
                  reactHookForm.setValue("withOwnerAnalyze", false);
                }
                if (key === "ZENBUJIKO" && !event) {
                  reactHookForm.setValue("withAllMattersAnalyze", false);
                }
              }}
            />
          </FormProvider>
        </Box>
      </Stack>
      {(ownerInfoViaScraping || displayCheckboxAllMattersAnalyze) && (
        <Box display={"flex"} flexDirection="column" gap={1}>
          {ownerInfoViaScraping && (
            <CCheckbox
              control={reactHookForm.control}
              name="withOwnerAnalyze"
              label="所有者事項の取得と同時に内容を解析する"
              disabled={!pictureCheckOptions.SHOYUSHAJIKO.checked}
            />
          )}
          {displayCheckboxAllMattersAnalyze && (
            <CCheckbox
              control={reactHookForm.control}
              name="withAllMattersAnalyze"
              label="全部事項の取得と同時に内容を解析する（テスト版）"
              disabled={!pictureCheckOptions.ZENBUJIKO.checked}
            />
          )}
        </Box>
      )}
      <Box
        sx={{
          mx: "auto",
          my: 4,
          display: "flex",
          justifyContent: "center",
          gap: "0 1em",
        }}
      >
        {chibanKaokuNumbers.length > 1 ? (
          <Button
            label="不動産登記簿の一括取得に進む"
            onClick={() => {
              void (async () => {
                // 必要な項目のバリデーションを実行する
                const isValid = await reactHookForm.trigger([
                  "realEstateTypeEng",
                  "prefectureId",
                  "location",
                  "pastDays",
                ]);

                if (isValid) {
                  // バリデーション成功時の処理
                  const identifyName = reactHookForm.getValues("identifyName");
                  const data = createAcquireMultipleBookHandoffData(
                    realEstateTypeEng as BookTypeEng,
                    chibanKaokuNumbers.reverse(), // 入力順と逆にする
                    prefectureId,
                    prefectureName,
                    location,
                    []
                  );

                  const stateData: AcquireMultipleBookLocationState = {
                    data,
                    identifyName,
                    pastDays,
                  };

                  // 一括取得の画面に地番を含めて値を受け渡し、登記取得一括画面に遷移する。
                  navigate("/acquiremultiplebooks", {
                    state: stateData,
                  });
                }
              })();
            }}
            variant={ButtonVariantOption.Contained}
            size={SizingWrapperStyle.MEDIUM}
          />
        ) : (
          <Button
            variant={ButtonVariantOption.Contained}
            onClick={() => {
              void (async () => {
                // 全体のバリデーションを実行
                const isValid = await reactHookForm.trigger();
                if (isValid) {
                  setOpenModalFlag(true);
                }
              })();
            }}
            label={"取得する"}
          />
        )}
        {handleClose ? (
          <Button
            variant={ButtonVariantOption.Outlined}
            onClick={() => {
              handleClose();
            }}
            disabled={disableConfirmModalCancelButton}
            label={"キャンセル"}
          />
        ) : null}
      </Box>

      {/* 所在選択ダイアログ */}
      <SelectLocationModal
        open={selectLocationDialogIsOpen}
        onClose={() => {
          setSelectLocationDialogIsOpen(false);
        }}
        prefectureId={Number(prefectureId)}
        setLocationWithoutCity={(location: string) => {
          reactHookForm.setValue("location", location);
          void reactHookForm.trigger("location"); // バリデーションを明示的に再実行
        }}
      />

      {/* 確認のモーダル画面 */}
      <ConfirmAcquireBookDialog
        open={openModalFlag}
        setOpen={setOpenModalFlag}
        pictureCheckOptions={pictureCheckOptions}
        handleAcquireButtonClick={handleAcquireButtonClick}
        disableConfirmModalCancelButton={disableConfirmModalCancelButton}
      />

      {/* 家屋番号検索モーダル */}
      <Modal
        open={kaokuNumberSearchModalIsOpen}
        aria-labelledby={"kaoku-number-search-modal"}
        aria-describedby={"kaoku-number-search-modal"}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: 1600,
              maxWidth: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <KaokuNumberSearchModal
              chibanList={chibanKaokuNumbers.map((chibanKaokuNumber) => ({
                prefectureName,
                location,
                chiban: chibanKaokuNumber,
              }))}
              setKaokuNumberSearchModalIsOpen={setKaokuNumberSearchModalIsOpen}
              appendPropertySelectionRows={appendPropertySelectionRows}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
